import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Grid,
  Divider,
  Link,
} from "@mui/material";
import { Shield, Lock, Users, FileText, Globe, Mail } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight={700}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Effective Date: October 15, 2025 · Last Updated: October 15, 2025
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Main */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
          }}
        >
          <Stack spacing={6}>
            {/* Introduction */}
            <Section icon={<FileText />} title="Introduction">
              <Typography>
                Welcome to <strong>LTC Tourist eSIM</strong>. This Privacy
                Policy explains how we collect, use, disclose, and safeguard
                your information when you use our services and website at{" "}
                <Link href="https://esim.laotel.com/" underline="hover">
                  https://esim.laotel.com/
                </Link>
                .
              </Typography>
              <Typography>
                By using our services, you agree to the collection and use of
                information in accordance with this policy.
              </Typography>
            </Section>

            {/* Information We Collect */}
            <Section icon={<Users />} title="1. Information We Collect">
              <InfoCard title="1.1 Personal Information">
                <BulletList
                  items={[
                    "Full name",
                    "Email address",
                    "Phone number",
                    "Passport or national ID (verification)",
                    "Payment information (via secure third parties)",
                    "Device information (IMEI, model, OS)",
                  ]}
                />
              </InfoCard>

              <InfoCard title="1.2 Usage Data">
                <BulletList
                  items={[
                    "Data usage statistics",
                    "Connection logs",
                    "Network performance data",
                    "Approximate location data",
                    "Service usage patterns",
                  ]}
                />
              </InfoCard>

              <InfoCard title="1.3 Technical Data">
                <BulletList
                  items={[
                    "IP address",
                    "Browser and device identifiers",
                    "Cookies and similar technologies",
                  ]}
                />
              </InfoCard>
            </Section>

            {/* How We Use */}
            <Section title="2. How We Use Your Information">
              <Grid container spacing={2}>
                {[
                  ["Service Provision", "Activating and managing your eSIM"],
                  ["Customer Support", "Handling inquiries and assistance"],
                  ["Billing", "Payment processing and invoices"],
                  ["Service Improvement", "Analytics and optimization"],
                  ["Communication", "Updates and promotions"],
                  ["Security", "Fraud and abuse prevention"],
                  ["Legal Compliance", "Regulatory obligations"],
                ].map(([title, desc]) => (
                  <Grid item xs={12} key={title}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Typography fontWeight={600}>{title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {desc}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Section>

            {/* Data Security */}
            <Section icon={<Lock />} title="5. Data Security">
              <Grid container spacing={2}>
                {[
                  "Encryption of data in transit and at rest",
                  "Secure authentication mechanisms",
                  "Regular security audits",
                  "Access controls and training",
                  "PCI-DSS compliant payment processing",
                ].map((item) => (
                  <Grid item xs={12} md={6} key={item}>
                    <Paper
                      sx={{
                        p: 2,
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                        bgcolor: "success.50",
                      }}
                    >
                      <Shield size={18} />
                      <Typography variant="body2">{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="caption" color="text.secondary">
                No system is completely secure. Absolute security cannot be
                guaranteed.
              </Typography>
            </Section>

            {/* Contact */}
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: "primary.50",
                border: "1px solid",
                borderColor: "primary.200",
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Mail size={22} />
                  <Typography variant="h5" fontWeight={700}>
                    Contact Us
                  </Typography>
                </Stack>

                <Typography>
                  If you have questions or concerns regarding this policy:
                </Typography>

                <Divider />

                <Typography fontWeight={600}>
                  Lao Telecommunications Co., Ltd
                </Typography>

                <Typography>Tel: +856 20 52607789</Typography>
                <Typography>
                  Email:{" "}
                  <Link href="mailto:support@laotel.com">
                    support@laotel.com
                  </Link>
                </Typography>
              </Stack>
            </Paper>

            {/* Footer */}
            <Divider />
            <Typography variant="caption" textAlign="center">
              Document Version 1.0 · English (Lao version available upon
              request)
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

/* ---------- Helpers ---------- */

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Stack>
  );
}

function InfoCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Typography fontWeight={600} mb={1}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <Stack component="ul" spacing={0.5} sx={{ pl: 2 }}>
      {items.map((item) => (
        <Typography component="li" variant="body2" key={item}>
          {item}
        </Typography>
      ))}
    </Stack>
  );
}
